<template>
  <div class="prototype-page">
    <loader v-if="getIsProjectLoading" :message="'Loading prototype...'" />
    <!-- Navbar -->
    <transition name="fade">
      <prototype-navigation
        v-show="navActive == true"
        :active="navActive"
        :audio-error="showAudioTooltip"
        @mouseover="navActive == true"
      />
    </transition>
    <div
      v-show="projectLoaded && (getStartingElement || queryElementId)"
      ref="proto"
      class="proto"
    >
      <div
        v-if="projectLoaded && (getStartingElement || queryElementId)"
        class="wrapper"
        @click.self="onClickWrapper"
      >
        <!-- Restart Play Mode Btn -->
        <transition name="fade">
          <div
            v-show="navActive == true"
            class="restart"
            @click="onClickRestart"
          >
            Restart
          </div>
        </transition>
        <div class="page" @click.stop="onClickPage">
          <div class="top">
            <transition
              name="slow-fade"
              mode="out-in"
              @after-leave="onAfterLeave"
            >
              <section
                v-if="
                  isCoverEmbed ||
                  coverUrl ||
                  icon ||
                  (components && components.length > 0)
                "
                class="media-container"
              >
                <transition
                  name="slow-fade"
                  mode="out-in"
                  @after-leave="onAfterLeave"
                >
                  <embeded-youtube
                    v-if="isCoverEmbed"
                    :key="embededUrl"
                    :url="embededUrl"
                    :autoplay="embededAutoplay"
                    :show-controls="embededShowControls"
                    class="embeded-youtube"
                  />

                  <ProgressiveImage
                    v-else-if="coverUrl"
                    :key="coverUrl"
                    class="single"
                    :src="coverUrl"
                    :placeholder-src="coverPlaceholderUrl"
                  />

                  <svg-icon
                    v-else-if="icon"
                    :key="icon"
                    :icon="icon"
                    :width="'196'"
                    :height="'196'"
                  />
                  <div
                    v-else-if="components && components.length > 0"
                    :key="JSON.stringify(components)"
                    :style="
                      'grid-template-columns: repeat(' +
                      gridSize +
                      ', minmax(0, 1fr))'
                    "
                    class="components-container"
                    :class="{
                      single:
                        components && components.length === 1 && !hasCover,
                    }"
                  >
                    <prototype-component
                      v-for="(component, index) in components"
                      :id="component.id"
                      :key="index"
                      :default-aspect="
                        components && components.length === 1 && !hasCover
                      "
                    />
                  </div>
                </transition>
              </section>
            </transition>

            <transition
              appear
              name="slow-fade"
              mode="out-in"
              @after-leave="onAfterLeave"
            >
              <div
                v-if="components && components.length > 0 && hasCover"
                style="position: relative; z-index: 1"
              >
                <section
                  class="components-container"
                  :class="{
                    'components-container-pushed': coverUrl,
                    'icon-pushed': icon || embededUrl,
                  }"
                  :style="
                    'grid-template-columns: repeat(' +
                    gridSize +
                    ', minmax(0, 1fr))'
                  "
                >
                  <prototype-component
                    v-for="(component, index) in components"
                    :id="component.id"
                    :key="index"
                    :default-aspect="
                      components && components.length === 1 && !hasCover
                    "
                  />
                  <div class="flex-fix" />
                </section>
              </div>
            </transition>
          </div>
          <transition
            name="slow-fade"
            mode="out-in"
            @after-leave="onAfterLeave"
          >
            <section
              :key="renderedContent"
              class="prototype-content"
              :class="{ showArrow: showArrow }"
            >
              <rich-text-editor
                ref="contentEditor"
                class="container"
                :content="renderedContent"
                :editable="false"
                :activate-on-double-click="false"
                :select-on-double-click="false"
                :extension-options="extensionOptions"
              />
            </section>
          </transition>

          <transition
            name="slow-fade"
            mode="out-in"
            @after-leave="onAfterLeave"
          >
            <div :key="currentElementId + '_' + visits">
              <draggable
                v-if="!showArrow"
                v-bind="dragOptions"
                :list="options"
                item-key="id"
                class="option-container ui-sortable"
                @start="drag = true"
                @end="onOptionDrop"
              >
                <template #item="{ element }">
                  <prototype-option
                    :text="element.text"
                    :drag="drag"
                    @click-option="onClickOption(element)"
                  />
                </template>
              </draggable>
            </div>
          </transition>

          <transition name="slow-fade" mode="out-in">
            <div v-if="contentError" class="error">
              {{ contentError }}
            </div>
          </transition>
          <transition name="slow-fade" mode="out-in">
            <div v-if="optionErrors.length > 0">
              <div
                v-for="(optionError, errorIndex) in optionErrors"
                :key="errorIndex"
                class="error"
              >
                {{ optionError }}
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
    <div
      v-if="projectLoaded && !getStartingElement && !queryElementId"
      class="invalid"
    >
      <div class="container">
        To enable Play Mode please set one of your project elements as the
        Starting element <br /><br />
        <span>
          ( Right click on an element and select Set Play Mode start )
        </span>
      </div>
    </div>
    <!-- Debug component -->
    <transition>
      <prototype-debug-console
        v-show="getDebugSection"
        :initial-before-values="initialBeforeValues"
        :before-values="beforeValues"
        @update-before-values="updateBeforeValues"
        @close-debug="debug = false"
        @click-back="onClickBack"
        @update-before-value="onUpdateBeforeValue"
      />
    </transition>
    <div ref="audio" class="audio" />
  </div>
</template>

<script>
import 'overlayscrollbars/overlayscrollbars.css';
import {
  OverlayScrollbars,
  ScrollbarsHidingPlugin,
  SizeObserverPlugin,
} from 'overlayscrollbars';
import draggable from 'vuedraggable';
import { mapGetters, mapActions, mapMutations } from 'vuex';
import _ from 'lodash';
import { Interpreter, RuntimeError, ParseError } from '@arcweave/arcscript';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS } from '@/constants.js';
import Loader from '../all/Loader.vue';
import SvgIcon from '../all/SvgIcon.vue';
import PrototypeNavigation from './PrototypeNavigation.vue';
import PrototypeComponent from './PrototypeComponent.vue';
import PrototypeOption from './PrototypeOption.vue';
import PrototypeDebugConsole from './PrototypeDebugConsole.vue';
import RichTextEditor from '../all/RichText/RichTextEditor.vue';
import { unwrapMention } from '../../vuex/project/mention-helper.js';
import {
  removeCodeElements,
  removeEnclosingParagraph,
  truncateString,
  removeEmpty,
} from '../../utils.js';
import EmbededYoutube from '../all/EmbededYoutube.vue';

draggable.compatConfig = { MODE: 3 };

export default {
  components: {
    draggable,
    Loader,
    PrototypeNavigation,
    PrototypeComponent,
    PrototypeOption,
    PrototypeDebugConsole,
    RichTextEditor,
    SvgIcon,
    EmbededYoutube,
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      projectLoaded: false,
      navActive: false,
      currentElementId: null,
      queryElementId: null,
      drag: false,
      contentError: null,
      optionErrors: [],
      visits: 0,
      renderedContent: null,
      initialBeforeValues: {}, // Variable ids and values before rendering
      beforeValues: {}, // Variable ids and values before rendering (merged with state changes)
      stateChanges: {}, // Any user-defined state changes (due to modifications in values in debug)
      loadedAudioAssets: new Set(),
      showAudioTooltip: false,
      audioNodes: {},
      audioToRef: {},
      actx: null,
      masterGainNode: null,
      currentPlayingAssets: new Set(),
      visitsInitialized: false,
      osInstance: null,
      refreshOptions: 0, // Force update options computed property by changing this value
    };
  },

  computed: {
    ...mapGetters('application/appStates', [
      'getIsProjectLoading',
      'getIsProjectLoaded',
    ]),
    ...mapGetters('project/data/variables', [
      'getDefaultValues',
      'getVariables',
    ]),
    ...mapGetters('playMode', [
      'getValues',
      'getLastEntry',
      'getEntriesNumber',
      'getAudioLevel',
      'getVisits',
      'getAllVisits',
    ]),
    ...mapGetters('project/data/elements', {
      getComponents: 'getComponents',
      getElement: 'getNode',
      getElementIds: 'getNodeIds',
      getStartingElement: 'getStartingElement',
      getCover: 'getCover',
      getCoverId: 'getCoverId',
      getCoverIcon: 'getCoverIcon',
      getOutputs: 'getOutputs',
      getAssets: 'getAssets',
      getAudioReferencesExist: 'getAudioReferencesExist',
    }),
    ...mapGetters('project/data/jumpers', {
      getJumper: 'getNode',
    }),
    ...mapGetters('project/data/components', {
      getNodes: 'getNodes',
    }),
    ...mapGetters('project/data/connections', {
      getConnection: 'getNode',
      getConnections: 'getConnections',
    }),
    ...mapGetters('project/data/branches', ['getTruthyCondition']),
    ...mapGetters('project/data/assets', {
      getAsset: 'getNode',
      getAudioAssets: 'getAudioAssets',
      getFullImageUrl: 'getFullImageUrl',
      getTinyImageUrl: 'getTinyImageUrl',
      getAudioUrl: 'getAudioUrl',
    }),
    ...mapGetters('application', ['getDebugSection']),
    ...mapGetters('application/modals', ['getPrototypeAudioModal']),

    /**
     * Return the extensions to load on a rich text editor
     * @returns {Object}
     */
    extensionOptions() {
      return {
        hardbreak: true,
        bold: true,
        italic: true,
        underline: true,
        horizontalRule: false,
        customLink: true,
        history: true,
        placeholder: {
          text: this.hasCover ? '' : '[This element has no content]',
        },
        dropEventHandler: true,
        customMention: false,
      };
    },

    gridSize() {
      if (!this.hasCover) return this.components.length;
      return Math.max(this.components.length, 5);
    },

    /**
     * Return whether the blinking arrow instead of the options is shown
     * @returns {boolean}
     */
    showArrow() {
      if (this.options?.length === 1 && !this.options[0].label) {
        return true;
      }
      return false;
    },

    /**
     * Return the draggable options
     * @returns {Object}
     */
    dragOptions() {
      return {
        animation: 200,
        group: 'option',
        disabled: !this.canEdit,
        ghostClass: 'ghost',
        touchStartThreshold: 5,
        fallbackTolerance: 5,
      };
    },

    /**
     * Return the element cover asset id
     * @returns {String}
     */
    coverId() {
      return this.getCoverId(this.currentElementId);
    },

    /**
     * Return the url of the full image of the cover asset
     * @returns {String}
     */
    coverUrl() {
      return this.getFullImageUrl(this.coverId);
    },

    /**
     * Return true if the current element's cover is an embeded video
     * @returns {Boolean}
     */
    isCoverEmbed() {
      return this.getCover(this.currentElementId)?.type === 'youtube';
    },

    /**
     * Returns the Cover URL
     */
    embededUrl() {
      return this.getCover(this.currentElementId).url;
    },

    /**
     * Returns the Cover URL
     */
    embededAutoplay() {
      return this.getCover(this.currentElementId).autoplay === undefined
        ? true
        : this.getCover(this.currentElementId).autoplay;
    },

    /**
     * Returns the Cover URL
     */
    embededShowControls() {
      return this.getCover(this.currentElementId).showControls === undefined
        ? false
        : this.getCover(this.currentElementId).showControls;
    },

    /**
     * Return the element icon
     * @return {String}
     */
    icon() {
      return this.getCoverIcon(this.currentElementId);
    },

    /**
     * Return the url of the tiny image of the cover asset
     * @returns {String}
     */
    coverPlaceholderUrl() {
      return this.getTinyImageUrl(this.coverId);
    },

    /**
     * Returns if the element has cover.
     * @returns {Boolean}
     */
    hasCover() {
      return !!this.icon || !!this.isCoverEmbed || !!this.coverUrl;
    },

    /**
     * Return the components found in the component list of this element
     * @returns {array}
     */
    components() {
      if (!this.currentElementId) return null;
      const componentIds = this.getComponents(this.currentElementId).map(
        item => item.component
      );
      return this.getNodes(componentIds);
    },

    /**
     * Returns the audio assets found in the asset list of this element
     * @returns {array}
     */
    audioAssets() {
      if (!this.currentElementId) return null;
      const assets = this.getAssets(this.currentElementId);
      if (assets && assets.audio && Array.isArray(assets.audio)) {
        return assets.audio;
      }
      return null;
    },

    /**
     * Return the current options
     * @return {array}    Each option is an object { id, elementId, text } (id is the connection id)
     */
    options() {
      if (!this.projectLoaded) return null;
      if (!this.currentElementId) return null;

      // We are adding this to force update the options computed property
      // eslint-disable-next-line no-unused-expressions
      this.refreshOptions;

      this.clearOptionErrors();

      // Not all element outputs are valid options so we are using flatMap instead of map
      const result = this.getOutputs(this.currentElementId).flatMap(id => {
        let { targetid, targetType, label } = this.getConnection(id);
        let elementId = null;
        let finalLabel = null;

        if (!this.visitsInitialized) {
          this.resetVisits();
          this.visitsInitialized = true;
        }
        // We need one interpreter instance
        const interpreter = new Interpreter(
          this.getValues,
          this.getVariables,
          this.getAllVisits,
          this.currentElementId
        );
        let output = '';
        let changes = {};
        try {
          // Handle connection to a branch (and further branching)

          while (targetType === 'branches') {
            if (label) {
              ({ output, changes } = interpreter.runScript(label, changes));
              if (output) {
                finalLabel = output;
              }
            }
            const condition = this.getTruthyCondition(
              targetid,
              interpreter,
              changes
            );
            // Discard false conditions and conditions with no output
            if (!condition?.output) return [];

            const { output: conditionOutput } = condition;
            // Load the data for the next connection
            ({ targetid, targetType, label } =
              this.getConnection(conditionOutput));
          }

          // Handle the last label of the path (to a non-branch node)
          if (label) {
            ({ output, changes } = interpreter.runScript(label, changes));
            if (output) {
              finalLabel = output;
            }
          }
        } catch (error) {
          // Discard connections with parse errors and display the error message
          if (error instanceof RuntimeError || error instanceof ParseError) {
            this.addOptionError(`${error.name}: ${error.message}`);
            return [];
          }
        }

        // Handle connections to other types
        switch (targetType) {
          case 'elements': {
            elementId = targetid;
            break;
          }
          case 'jumpers': {
            console.log('Its a jumper with id', targetid);
            ({ elementId } = this.getJumper(targetid));
            break;
          }
          default:
            throw new Error('Invalid targetType');
        }
        // If no element found discard this output from flatmap
        if (!elementId) return [];
        let { title, content } = this.getElement(elementId);
        title = removeEnclosingParagraph(title);
        title = unwrapMention(title, null, 'component');
        title = unwrapMention(title, null, 'board');
        content = removeEnclosingParagraph(removeCodeElements(content));
        content = unwrapMention(content, null, 'component');
        content = unwrapMention(content, null, 'board');
        // The option text will be the first valid value from below
        const text =
          finalLabel ||
          title ||
          truncateString(content, 70) ||
          '[ Empty element ]';
        console.log('FlatMap returns text:', text);
        console.log('Title is:', title);
        // The flatMap returns a value for this output
        return [
          {
            id,
            elementId,
            text,
            label: finalLabel,
            changes: _.cloneDeep(changes),
          },
        ];
      });

      return result;
    },

    /**
     * Return the current element connections
     * @return {array}
     */
    elementConnections() {
      if (!this.currentElementId) return null;
      return this.getConnections(this.currentElementId).filter(id => {
        const connection = this.getConnection(id);
        return connection.sourceid === this.currentElementId;
      });
    },

    /**
     * Returns the current element
     * @return {object} The element content and the element id
     */
    elementContent() {
      if (!this.currentElementId) return '';
      const { content, id } = this.getElement(this.currentElementId);
      return { content, id };
    },

    /**
     * Returns the variable types and values
     * @return {object} An object of the form: id: { type, value }
     */
    variableTypes() {
      return Object.fromEntries(
        Object.values(this.getVariables)
          .filter(v => !v.children)
          .map(v => [v.id, { type: v.type, value: v.value }])
      );
    },
  },

  watch: {
    /**
     * The starting element has changed
     */
    getStartingElement(newValue, oldValue) {
      // Perform a re-render due to starting element change only if there is no start from specific element
      if (!this.$route.query.elem) {
        this.currentElementId = newValue;
        this.clearEntries();
        if (this.getIsProjectLoaded) {
          // Initialize audio assets
          const assets = this.getAssets(this.currentElementId);
          if (assets && assets.audio && Array.isArray(assets.audio)) {
            this.addAudioAssets(assets.audio);
          }
          this.renderElement(this.currentElementId, null, true, true);
        }
      }
    },

    /**
     * The element has changed, re-render the element
     */
    elementContent(newValue, oldValue) {
      if (
        newValue.id === oldValue.id &&
        newValue.content !== oldValue.content
      ) {
        this.renderElement(this.currentElementId, null, true);
      }
    },

    /**
     * One or more variable has its type (or default value) changed
     */
    variableTypes(newValue, oldValue) {
      // Find which variables had their type changed
      const changed = Object.fromEntries(
        Object.entries(newValue)
          .filter(([id, v]) => v.type !== oldValue[id]?.type)
          .map(([id, v]) => [id, v.value])
      );
      // Adjust initialBefore values with the values
      this.initialBeforeValues = { ...this.initialBeforeValues, ...changed };
      this.beforeValues = { ...this.beforeValues, ...changed };
      this.setValues({ values: changed });

      // Refresh the current element
      // this.renderElement(this.currentElementId, null, true);
    },

    /**
     * Update the audio nodes when the audio level changes
     */
    getAudioLevel(newValue, oldValue) {
      this.masterGainNode.gain.value = newValue;
    },

    getAudioAssets: {
      handler(newValue, oldValue) {
        newValue.forEach(audioAsset => {
          const qs = this.$refs.audio.querySelectorAll(
            `audio[data-asset="${audioAsset.id}"]`
          );
          qs.forEach(audioNode => {
            audioNode.volume = audioAsset?.volume ?? 1;
          });
        });
      },
      deep: true,
    },

    options(newValue, oldValue) {
      const nextAudioAssets = [];
      if (!newValue) return;
      newValue.forEach(option => {
        const assets = this.getAssets(option.elementId);
        if (assets && assets.audio && Array.isArray(assets.audio)) {
          nextAudioAssets.push(...assets.audio);
        }
      });

      this.addAudioAssets(nextAudioAssets);
    },
  },

  /**
   * Vue lifecycle created hook
   */
  created() {
    const { hash } = this.$route.params;
    OverlayScrollbars.plugin([ScrollbarsHidingPlugin, SizeObserverPlugin]);
    // Load the project
    this.showProject(this.$route.params.hash, this.$route.query.elem);
  },

  /**
   * Vue lifecycle unmounted hook
   */
  unmounted() {
    this.leaveProjectUpdateChannel({ hash: this.$route.params.hash });
  },

  mounted() {
    // eslint-disable-next-line no-undef
    this.actx = new (AudioContext || webkitAudioContext)();
    this.masterGainNode = this.actx.createGain();
    this.masterGainNode.connect(this.actx.destination);
  },

  methods: {
    ...mapActions(['loadProject']),
    ...mapActions('application/channels', [
      'startProjectUpdateChannel',
      'leaveProjectUpdateChannel',
    ]),
    ...mapActions('project/data/elements', ['moveOutput']),
    ...mapMutations('playMode', [
      'setValues',
      'pushEntry',
      'updateEntry',
      'popEntry',
      'clearEntries',
      'initVisits',
      'setVisits',
      'incrementVisits',
      'decrementVisits',
    ]),
    ...mapActions('application/modals', ['setPrototypeAudioModal']),

    /**
     * Event handler for custom event updateBeforeValues
     * @param {Object} values The variable values the user wants to update
     */
    updateBeforeValues(values) {
      // Store the updates
      this.stateChanges = { ...this.stateChanges, ...values };
      // Refresh the current element
      this.renderElement(this.currentElementId, null, true);
    },

    /**
     * Event handler for custom event 'updateBeforeValue' emitted by debug console
     * @param {string} id The variable id
     * @param {mixed} value The new value before the render
     */
    onUpdateBeforeValue({ id, value }) {
      // Store the updates
      this.stateChanges = { ...this.stateChanges, ...{ [id]: value } };
      // Refresh the current element
      this.renderElement(this.currentElementId, null, true);
    },

    /**
     * Load the project and display starting element
     * @param {string}  hash  The project to display
     */
    showProject(hash, startingElementId = null) {
      this.loadProject({ hash })
        .then(response => {
          this.startProjectUpdateChannel({ hash });

          if (
            this.getAudioReferencesExist &&
            !document.referrer.startsWith(document.location.origin)
          ) {
            this.setPrototypeAudioModal({
              value: {
                onClose: () => {
                  this.playAudio();
                },
              },
            });
          }
          // Initialize variables with the default values
          this.setValues({ values: this.getDefaultValues });
          this.resetVisits();
          this.visitsInitialized = true;
          // Start from the beginning
          if (
            startingElementId !== null &&
            this.getElement(startingElementId) !== null
          ) {
            this.currentElementId = startingElementId;
            this.queryElementId = startingElementId;
          } else {
            this.currentElementId = this.getStartingElement;
          }
          // Clear any play mode history
          this.clearEntries();
          // Initialize audio assets
          const assets = this.getAssets(this.currentElementId);
          if (assets && assets.audio && Array.isArray(assets.audio)) {
            this.addAudioAssets(assets.audio);
          }
          this.incrementVisits({ id: this.currentElementId });
          this.renderElement(this.currentElementId, null);

          this.$nextTick(() => {
            this.projectLoaded = true;
            this.osInstance = OverlayScrollbars(
              { target: this.$refs.proto },
              {
                scrollbars: {
                  autoHide: 'move',
                  autoHideDelay: 600,
                },
              }
            );
          });
        })
        .catch(error => {
          if (error.response) {
            const { status } = error.response.data;
            if (status === 404) {
              this.$router.replace('/project-error/404');
            } else if (status === 403) {
              this.$router.replace('/project-error/403');
            }
          }
        });
    },

    /**
     * Event handler: The user clicks on one of the options
     * @param {Object}  option  The option the user clicked on { id, elementId, text, label }
     */
    onClickOption(option) {
      this.selectOption(option);
    },

    /**
     * Move flow to the selected option
     * @param {Object} option The option { id, elementId, text, label }
     */
    selectOption(option) {
      // To allow loops using branches
      if (this.currentElementId === option.elementId) {
        this.currentElementId = null;
      }
      this.currentElementId = option.elementId;
      this.visits += 1; // Change the key so that the current element is re-rendered with animation
      this.incrementVisits({ id: option.elementId });
      this.renderElement(
        option.elementId,
        option.id,
        false,
        false,
        option.changes
      );
    },

    /**
     * Event handler: The user clicks on the restart button
     */
    onClickRestart() {
      // Reset the application variables - values
      this.setValues({ values: this.getDefaultValues });
      this.beforeValues = this.getDefaultValues;
      this.initialBeforeValues = this.beforeValues;
      this.resetVisits();

      // Change this to force the options computed property to update
      this.refreshOptions += 1;

      // Clear play mode history
      this.clearEntries();

      const startingElementId = this.queryElementId
        ? this.queryElementId
        : this.getStartingElement;

      // Start from the beggining
      this.currentElementId = startingElementId;
      // Initialize audio assets

      const assets = this.getAssets(this.currentElementId);
      if (assets && assets.audio && Array.isArray(assets.audio)) {
        this.addAudioAssets(assets.audio);
      }

      this.incrementVisits({ id: startingElementId });
      this.renderElement(startingElementId, null, false, true);
      this.navActive = false;
    },

    /**
     * Event handler: The user clicks anywhere on the page
     */
    onClickWrapper() {
      this.navActive = !this.navActive;
    },

    /**
     * Event handler: The user clicks inside the prototype content
     */
    onClickPage() {
      // Automatically move the flow to the only option available
      if (this.showArrow && this.options && this.options.length === 1) {
        const option = this.options[0];
        this.selectOption(option);
      }
    },

    /**
     * Event hanlder: draggable end event. The user moved an option to another index.
     */
    onOptionDrop(event) {
      const outputs = this.getOutputs(this.currentElementId);

      this.drag = false;
      const sId = this.currentElementId;

      // Get the new position of the moved option
      const { newIndex } = event;

      // Find the option on whose place it was moved
      const sIndex = outputs.findIndex(
        output => output === this.options[newIndex].id
      );
      let tIndex = null;
      if (this.options.length > newIndex + 1) {
        tIndex = outputs.findIndex(
          output => output === this.options[newIndex + 1].id
        );
      } else {
        tIndex = outputs.length;
      }
      // Draggable event has the new index already adjusted
      this.moveOutput({ sId, sIndex, tIndex, apply: true });
    },

    /**
     * Event handler: The user clicks on the back button in the debug console
     */
    onClickBack() {
      if (this.getEntriesNumber > 1) {
        // Remove the last (current) history element
        this.popEntry();
        // Get the last history element
        const entry = _.cloneDeep(this.getLastEntry);
        // Remove this entry to recreate it
        this.popEntry();
        const prevElementId = this.currentElementId;
        this.currentElementId = entry.elementId;
        this.renderElement(entry.elementId, entry.optionId);
        this.decrementVisits({ id: prevElementId });
      }
      this.navActive = false;
    },

    /**
     * Renders an element on the prototype page. We apply the arcscript code found on the element.
     * The current variable state is updated and the element is stored in play mode history.
     * @param {string} elementId The element id we are rendering
     * @param {string} optionId The connection (option) that led to this element rendering. Useful to store in history.
     * @param {boolean} refresh If this rendering is a refresh or was initiated by a user option
     * @param {boolean} restart If this rendering is from the restart button
     * @param {object} optionChanges Any state changes that are coming from the option(s) that led to this element
     */
    renderElement(
      elementId,
      optionId,
      refresh = false,
      restart = false,
      optionChanges = {}
    ) {
      console.log('RenderElement, option changes:', optionChanges);
      if (!elementId) return;
      this.setContentError(null);

      // Get the current element content
      let { content } = this.getElement(elementId);
      content = unwrapMention(content, null, 'component');
      content = unwrapMention(content, null, 'board');

      const modifications = {};
      try {
        const variables = this.getVariables;

        let previousElementValues = {};
        // If we are refreshing apply user-defined changes on current before values
        if (refresh) {
          // Our before values with user-defined changes
          this.beforeValues = { ...this.beforeValues, ...this.stateChanges };
          previousElementValues = { ...this.beforeValues };
        } else {
          previousElementValues = { ...this.getValues };
          // Use our current variable values
          this.beforeValues = { ...this.getValues, ...optionChanges };
          this.initialBeforeValues = { ...this.beforeValues };
          // Clear user-defined changes from a previous render
          this.stateChanges = {};
        }

        let output = null;
        let changes = {};
        if (content) {
          const interpreter = new Interpreter(
            this.beforeValues,
            variables,
            this.getAllVisits,
            elementId
          );
          ({ output, changes } = interpreter.runScript(content));
          console.log(changes);
        }
        // In case we have a refresh merge changes with (any) user-defined changes
        const applyChanges = {
          ...this.stateChanges,
          ...optionChanges,
          ...changes,
        };
        // Find the modifications to store in play mode history
        Object.entries(applyChanges).forEach(([id, value]) => {
          modifications[id] = {
            newValue: value,
            oldValue: previousElementValues[id],
          };
        });
        // Apply the changes
        this.setValues({ values: applyChanges });
        // Show the parsed content
        this.renderedContent = output;
      } catch (error) {
        if (error instanceof RuntimeError || error instanceof ParseError) {
          this.setContentError(`${error.name}: ${error.message}`);
        }
        console.error(error);
      }
      if (!this.contentError) {
        this.playAudio(restart);
      }
      // In case we do not re-render because of refresh create a history entry
      if (!refresh) {
        const entry = { elementId, optionId, modifications };
        this.pushEntry({ entry });
      } else {
        // We re-render, make sure to adjust any state modifications
        this.updateEntry({ modifications });
      }
    },

    addAudioAssets(audioAssets) {
      audioAssets.forEach(assetRef => {
        if (assetRef.id in this.audioNodes) {
          return;
        }
        if (assetRef.mode === 'stop') {
          return;
        }
        if (!(assetRef.asset in this.audioToRef)) {
          this.audioToRef[assetRef.asset] = [];
        }
        this.audioToRef[assetRef.asset].push(assetRef.id);
        const src = this.getAudioUrl(assetRef.asset);
        let el = null;

        if (this.loadedAudioAssets.has(assetRef.id)) {
          el = document.getElementById(`audio-asset-${assetRef.id}`);
        } else {
          this.loadedAudioAssets.add(assetRef.id);

          el = document.createElement('audio');
          el.setAttribute('id', `audio-asset-${assetRef.id}`);
          el.setAttribute('preload', 'metadata');
          el.setAttribute('src', src);
          if (assetRef.mode === 'loop') {
            el.setAttribute('loop', 'true');
          }
          el.setAttribute('data-asset', assetRef.asset);
          el.setAttribute('crossorigin', 'anonymous');
          this.$refs.audio.appendChild(el);
        }

        el.volume = this.getAsset(assetRef.asset)?.volume ?? 1;
        el.addEventListener('play', () => {
          this.currentPlayingAssets.add(assetRef.asset);
        });
        el.addEventListener('pause', () => {
          this.currentPlayingAssets.delete(assetRef.asset);
        });
        const source = this.actx.createMediaElementSource(el);

        let delay = null;
        if (assetRef.delay) {
          delay = this.actx.createDelay(179.9999);
          delay.delayTime.value = assetRef.delay / 1000;
          source.connect(delay).connect(this.masterGainNode);
        } else {
          source.connect(this.masterGainNode);
        }

        this.audioNodes[assetRef.id] = {
          src,
          source,
          delay,
        };
      });
    },

    playAudio(restart = false) {
      if (restart) {
        Object.values(this.audioNodes).forEach(audio => {
          audio.source.mediaElement.pause();
          audio.source.mediaElement.currentTime = 0;
          this.currentPlayingAssets.clear();
        });
      }

      if (!this.audioAssets) return;

      if (this.audioAssets.length > 0) {
        if (this.actx.state === 'suspended') {
          this.actx.resume();
        }
      }
      this.audioAssets.forEach(audioAsset => {
        if (audioAsset.mode === 'once') {
          this.audioNodes[audioAsset.id].source.mediaElement.play().catch(e => {
            console.error(e);
            if (e.name === 'NotSupportedError') {
              const arr = this.audioNodes[audioAsset.id].src.split('.');
              const type = arr[arr.length - 1];
              let content =
                'Unfortunately your browser does not support this file';
              if (type) {
                content = `Unfortunately your browser does not support ${type} files`;
              }
              this.setPrototypeAudioModal({
                value: {
                  content,
                  onClose: () => {},
                },
              });
            } else {
              this.setPrototypeAudioModal({
                value: {
                  onClose: () => {
                    this.playAudio();
                  },
                },
              });
            }
          });
        } else if (audioAsset.mode === 'loop') {
          if (!this.currentPlayingAssets.has(audioAsset.asset)) {
            this.audioNodes[audioAsset.id].source.mediaElement
              .play()
              .catch(e => {
                console.error(e);
                if (e.name === 'NotSupportedError') {
                  const arr = this.audioNodes[audioAsset.id].src.split('.');
                  const type = arr[arr.length - 1];
                  let content =
                    'Unfortunately your browser does not support this file';
                  if (type) {
                    content = `Unfortunately your browser does not support ${type} files`;
                  }
                  this.setPrototypeAudioModal({
                    value: {
                      content,
                      onClose: () => {},
                    },
                  });
                } else {
                  this.setPrototypeAudioModal({
                    value: {
                      onClose: () => {
                        this.playAudio();
                      },
                    },
                  });
                }
              });
          }
        } else if (audioAsset.mode === 'stop') {
          this.audioToRef[audioAsset.asset]?.forEach(id => {
            this.audioNodes[id].source.mediaElement.pause();
            this.audioNodes[id].source.mediaElement.currentTime = 0;
          });
        } else {
          console.error(
            `Audio Asset Reference mode "${audioAsset.mode}" not applicable.`
          );
        }
      });
    },

    resetVisits() {
      this.initVisits({ ids: this.getElementIds });
    },

    setContentError(message) {
      this.contentError = message;
    },

    clearOptionErrors() {
      this.optionErrors = [];
    },

    addOptionError(message) {
      this.optionErrors.push(message);
    },

    onAfterLeave() {
      this.$refs.proto.scrollTo({ top: 0 });
    },
  },
};
</script>

<style scoped lang="scss">
.prototype-page {
  display: flex;
  flex-flow: row;
  height: 100%;
  overflow: hidden;
  background: #000;
}
.invalid {
  background: #0d0e0e;
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  width: 100%;
  .container {
    color: #bcbcbc;
    text-align: center;
    font-size: 16px;
    span {
      opacity: 0.6;
    }
  }
}
.proto {
  background: #000; // #0d0e0e;
  flex: 1;
  display: flex;
  flex-flow: column;
  max-height: 100%;
  overflow-y: auto; /* Firefox fix (not supporting overflow:overlay) */
  .wrapper {
    visibility: visible;
    transition: visibility 0.2s;
    min-height: 100%;
    display: flex;
    justify-content: center;
    &:hover {
      transition: visibility 0s 0.2s;
    }
  }
  .page {
    max-width: 860px;
    width: 100%;
    min-height: 100%;
    padding: 60px 20px 60px 20px;
    font-size: 20px;
    overflow-x: hidden;
    .top {
      position: relative;
      .media-container {
        display: flex;
        flex-flow: wrap;

        .embeded-youtube {
          width: 100%;
          display: flex;
          justify-content: center;
        }
        .svg-icon-container {
          margin: 0 auto;
        }
        .flex-fix {
          visibility: hidden;
          height: 0px;
          margin-bottom: 0px;
          flex: 1;
          align-self: flex-start;
        }

        img.single {
          max-height: 100%;

          max-width: 100%;
          width: auto;
          min-width: 0;
          min-height: 0;
          margin: 10px;
        }
      }
    }
    .prototype-content {
      cursor: default;
    }
  }
  @media (max-width: 740px) {
    .page {
      padding-top: 15px;
      padding-bottom: 15px;
    }
  }
}

// @media (max-aspect-ratio: 1/1) and (max-width: 480px) {
//   .media-container {
//     flex-direction: row;
//   }
// }

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slow-fade-enter-active,
.slow-fade-leave-active {
  transition: opacity 0.35s;
}
.slow-fade-enter-from,
.slow-fade-leave-to {
  opacity: 0;
}
.slow-fade-leave,
.slow-fade-enter-to {
  opacity: 1;
}

.move-enter-active,
.move-leave-active {
  transition: transform 0.25s;
}

.move-enter-from,
.move-leave-to {
  transform: translateY(100%);
}

.restart {
  position: fixed;
  color: #fff;
  border-radius: 999px;
  bottom: 20px;
  left: 15px;
  padding: 10px 20px;
  background: rgba(30, 33, 33, 0.8);
  z-index: 100;
  cursor: pointer;
  &:hover {
    background: rgba(30, 33, 33, 1);
  }
}

.error {
  border-radius: 3px;
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  margin: 10px 0 0 0;
  transition: all 0.5s ease;
  background: #8e3420;
  border: 1px solid #e23009;
  color: #fff;
  font-size: 14px;
}

.components-container {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-gap: 0 3%;
  width: 100%;
}

.components-container > .comp {
  display: flex;
  aspect-ratio: 1 / 1;
  border-radius: 4px;
  background: #1d1f1f;
}

.components-container.single > .comp {
  aspect-ratio: auto;
  background: none;
}

.components-container-pushed {
  padding: 0 3% 0 3%;
}

.icon-pushed > .comp {
  margin-top: 15px;
}

.components-container-pushed > .comp {
  z-index: 1;
  margin-top: -30%;
}
</style>

<style lang="scss">
// .option-list-move {
//   transition: transform 0.5s;
// }
.ghost {
  // opacity: 0.5;
  background: #292d2d !important;
}

.v-popper__popper {
  display: block !important;
  z-index: 210;

  .v-popper__inner {
    background: black;
    color: #bcbcbc;
    border-radius: 3px;
    padding: 5px 10px 4px;

    .shortcut {
      opacity: 0.6 !important;
      text-transform: uppercase;
      display: inline-block;
      margin: 0 0 0 5px;
      font-weight: 600;
    }
  }

  .tooltip-arrow {
    width: 0;
    height: 0;
    border-style: solid;
    position: absolute;
    margin: 5px;
    border-color: black;
    z-index: 1;
  }

  &[data-popper-placement^='top'] {
    margin-bottom: 10px;

    .tooltip-arrow {
      border-width: 5px 5px 0 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      bottom: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[data-popper-placement^='bottom'] {
    margin-top: 10px;

    .tooltip-arrow {
      border-width: 0 5px 5px 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-top-color: transparent !important;
      top: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[data-popper-placement^='right'] {
    margin-left: 10px;

    .tooltip-arrow {
      border-width: 5px 5px 5px 0;
      border-left-color: transparent !important;
      border-top-color: transparent !important;
      border-bottom-color: transparent !important;
      left: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &[data-popper-placement^='left'] {
    margin-right: 5px;

    .tooltip-arrow {
      border-width: 5px 0 5px 5px;
      border-top-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      right: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &[aria-hidden='true'] {
    visibility: hidden;
    opacity: 0;
    // transition: opacity 0.15s, visibility 0.15s;
  }

  &[aria-hidden='false'] {
    visibility: visible;
    opacity: 1;
    // transition: opacity 0.15s;
  }
}

.proto {
  .editor {
    a {
      padding-left: 20px;
      background-size: 14px;
      background-position: 1px 8px;
    }

    [data-placeholder] {
      min-height: 30px;
    }
  }

  .os-scrollbar {
    --os-handle-bg: #242829;
    --os-handle-bg-hover: #424a4c;
    --os-handle-bg-active: #424a4c;
  }
}

//TOOLTIP SECONDARY STYLES
.tooltip.secondary {
  .tooltip-inner {
    background: #1e2121;
  }
  .tooltip-arrow {
    border-color: #1e2121;
  }
}

// Arrow fix when element content is empty
.prototype-content.showArrow .editor p.is-editor-empty:first-child:before {
  float: none;
}

.prototype-content.showArrow .editor .ProseMirror > :last-child::after {
  content: '';
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 10px solid #db841e;
  display: inline-block;
  margin: 0 0 3px 14px;
  -webkit-animation: flickerAnimation 2s infinite;
  -moz-animation: flickerAnimation 2s infinite;
  -o-animation: flickerAnimation 2s infinite;
  animation: flickerAnimation 2s infinite;
}

@keyframes flickerAnimation {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
@-o-keyframes flickerAnimation {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
@-moz-keyframes flickerAnimation {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
@-webkit-keyframes flickerAnimation {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
